/**
 * Edit a campaign report page: label, reporting window, auto-refresh, whether the client
 * sees the booked goal, the derived brands (drive the advertiser hub's brand
 * chips — max 3), and which advertiser page lists it. PATCHes only the fields
 * that changed.
 */

import React, { useMemo, useState } from 'react';
import { Button, Modal } from '../../ui';
import {
  CampaignReportPage,
  ReportWindowMode,
  UpdateCampaignReportPageInput,
  updateCampaignReportPage,
} from '../../../services/campaignReportsApi';
import type { AdvertiserReportPage } from '../../../services/advertiserReportsApi';
import { Notify, campaignWindowMode, fieldStyle, helpStyle, isYmd, labelStyle, parseBrandNames, sameIdSet } from './shared';

interface CampaignEditModalProps {
  page: CampaignReportPage;
  /** Every advertiser page — the link control lists the ones covering this order. */
  hubs: AdvertiserReportPage[];
  onClose: () => void;
  onSaved: () => void;
  notify: Notify;
}

const CampaignEditModal: React.FC<CampaignEditModalProps> = ({ page, hubs, onClose, onSaved, notify }) => {
  const [label, setLabel] = useState(page.clientLabel);
  const [autoRefresh, setAutoRefresh] = useState(page.autoRefresh);
  const [windowMode, setWindowMode] = useState<ReportWindowMode>(campaignWindowMode(page));
  const [reportStart, setReportStart] = useState(page.reportStartDate);
  const [requestedEnd, setRequestedEnd] = useState(page.requestedEndDate);
  const [hideBookedGoal, setHideBookedGoal] = useState(page.hideBookedGoal === true);
  const [brandsText, setBrandsText] = useState((page.brandNames ?? []).join(', '));
  const [hubSlug, setHubSlug] = useState(page.advertiserPageSlug ?? '');
  const [saving, setSaving] = useState(false);

  // Hubs the backend will accept a link to: they cover the order's advertiser or include the order.
  const coveringHubs = useMemo(
    () =>
      hubs.filter(
        (h) => !h.revokedAt && (h.gamAdvertiserIds.includes(page.advertiserId) || h.includeOrderIds.includes(page.gamOrderId)),
      ),
    [hubs, page.advertiserId, page.gamOrderId],
  );
  const currentHubListed = !page.advertiserPageSlug || coveringHubs.some((h) => h.slug === page.advertiserPageSlug);
  const showHubControl = coveringHubs.length > 0 || !!page.advertiserPageSlug;

  const parsedBrands = parseBrandNames(brandsText);
  const brandsError = 'error' in parsedBrands ? parsedBrands.error : null;

  const save = async () => {
    if (brandsError) return notify('error', 'Brands', brandsError);
    if (windowMode === 'fixed' && (!isYmd(reportStart) || !isYmd(requestedEnd) || requestedEnd < reportStart)) {
      return notify('error', 'Reporting window', 'Enter valid start and end dates, with the end on or after the start.');
    }
    const patch: UpdateCampaignReportPageInput = {};
    if (label.trim() && label.trim() !== page.clientLabel) patch.clientLabel = label.trim();
    if (autoRefresh !== page.autoRefresh) patch.autoRefresh = autoRefresh;
    if (windowMode !== campaignWindowMode(page)) patch.reportingWindowMode = windowMode;
    if (windowMode === 'fixed') {
      if (reportStart !== page.reportStartDate) patch.reportStartDate = reportStart;
      if (requestedEnd !== page.requestedEndDate) patch.requestedEndDate = requestedEnd;
    }
    if (hideBookedGoal !== (page.hideBookedGoal === true)) patch.hideBookedGoal = hideBookedGoal;
    if ('brands' in parsedBrands && !sameIdSet(parsedBrands.brands, page.brandNames ?? [])) patch.brandNames = parsedBrands.brands;
    if (hubSlug !== (page.advertiserPageSlug ?? '')) patch.advertiserPageSlug = hubSlug || null;
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await updateCampaignReportPage(page.slug, patch);
      notify('success', 'Page updated');
      onSaved();
      onClose();
    } catch (err: any) {
      notify('error', 'Update failed', err?.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Campaign reports"
      title={`Edit — ${page.clientLabel}`}
      width={520}
      closeOnEsc={false}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={saving || !!brandsError}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 12.5, color: 'var(--fg-4)' }}>
          /campaigns/{page.slug}/report · {page.gamOrderName} — slug, order and PIN have their own flows.
        </div>
        <div>
          <label style={labelStyle}>Client label</label>
          <input style={fieldStyle} value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Brands (comma-separated)</label>
          <input
            style={fieldStyle}
            value={brandsText}
            onChange={(e) => setBrandsText(e.target.value)}
            placeholder="Sprite, Fanta"
          />
          <div style={{ ...helpStyle, color: brandsError ? 'var(--danger)' : helpStyle.color }}>
            {brandsError ??
              'Up to 3 brands — shown as chips on the advertiser page. Leave empty to fall back to the client label.'}
          </div>
        </div>
        {showHubControl && (
          <div>
            <label style={labelStyle}>Advertiser page</label>
            <select style={fieldStyle} value={hubSlug} onChange={(e) => setHubSlug(e.target.value)}>
              <option value="">— none (standalone page) —</option>
              {coveringHubs.map((h) => (
                <option key={h.slug} value={h.slug}>
                  {h.clientLabel} · /advertisers/{h.slug}/reports
                </option>
              ))}
              {!currentHubListed && page.advertiserPageSlug && (
                <option value={page.advertiserPageSlug}>current: /advertisers/{page.advertiserPageSlug}/reports</option>
              )}
            </select>
            <div style={helpStyle}>
              Listing the page on an advertiser hub also lets the hub’s passcode open this report. Only hubs covering this
              order’s advertiser are offered.
            </div>
          </div>
        )}
        <div>
          <label style={labelStyle}>Reporting window</label>
          <select style={fieldStyle} value={windowMode} onChange={(e) => setWindowMode(e.target.value as ReportWindowMode)}>
            <option value="follow_campaign">Follow campaign dates in GAM</option>
            <option value="fixed">Fixed reporting dates</option>
          </select>
          <div style={helpStyle}>Follow GAM to include extensions automatically, even after the original end date. Fixed dates keep a monthly or custom report unchanged.</div>
        </div>
        {windowMode === 'fixed' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={labelStyle}>Reporting start</label><input type="date" style={fieldStyle} value={reportStart} onChange={(e) => setReportStart(e.target.value)} /></div>
            <div><label style={labelStyle}>Reporting end</label><input type="date" style={fieldStyle} value={requestedEnd} onChange={(e) => setRequestedEnd(e.target.value)} /></div>
          </div>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
          <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
          Auto-refresh from GAM
        </label>
        <div style={helpStyle}>Impressions, clicks, CTR, Delivery Trends and device data refresh hourly through 14 days after the reporting end. Reach refreshes daily.</div>
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
            <input type="checkbox" checked={hideBookedGoal} onChange={(e) => setHideBookedGoal(e.target.checked)} />
            Hide the booked impression goal — show delivered impressions only
          </label>
          <div style={helpStyle}>
            The client page, its XLSX and the advertiser-hub card drop the booked goal and the pacing verdict and show the
            lifetime impressions delivered instead. Use it when the booked total in GAM no longer reflects the buy (e.g. a
            superseded line item that could not be changed mid-flight). Ad Ops still sees the real pacing here.
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CampaignEditModal;
